import { createThunk, ListModel, model, view } from 'mst-collection';
import { castToSnapshot, getRoot, types } from 'mobx-state-tree';

import {
  Api,
  IConfirmPaymentMethodRequestParams,
  IPaymentMethodResponse,
  schemas,
} from '@api';
import {
  PaymentMethod,
  PaymentMethodModel,
} from './PaymentMethodModel';
import type { RootModelType } from '../RootModel';

function processPaymentMethod(
  paymentMethodResponse: IPaymentMethodResponse,
) {
  return {
    ...paymentMethodResponse,
    id: paymentMethodResponse.paymentMethodId,
  };
}

export class PaymentMethodList extends ListModel(
  types.reference(PaymentMethodModel),
  {
    idAttribute: 'id',
    schema: schemas.PaymentMethodListSchema,
  },
) {
  fetch = createThunk(() => {
    return async function fetch(this: PaymentMethodList) {
      const res = await Api.Payments.getPaymentMethods();

      this.set(res.data.items.map((i) => processPaymentMethod(i)));
    };
  });

  setupPaymentMethod = createThunk(() => {
    return async function setupPaymentMethod(
      this: PaymentMethodList,
    ) {
      const res = await Api.Payments.setupPaymentMethod();

      return res.data;
    };
  });

  confirmPaymentMethod = createThunk(
    (params: IConfirmPaymentMethodRequestParams) => {
      return async function confirmPaymentMethod(
        this: PaymentMethodList,
      ) {
        const confirmRes = await Api.Payments.confirmPaymentMethod(
          params,
        );

        const paymentMethodRes = await Api.Payments.getPaymentMethod({
          id: confirmRes.data.paymentMethodId,
        });

        this.add(
          castToSnapshot(processPaymentMethod(paymentMethodRes.data)),
        );
      };
    },
  );

  fetchByIdAndMergeIntoEntity = createThunk((id: string) => {
    return async function fetchByIdAndMergeIntoEntity(flow) {
      const res = await Api.Payments.getPaymentMethod({ id });

      flow.merge(
        { ...res.data, id: res.data.paymentMethodId },
        schemas.PaymentMethodSchema,
      );
    };
  });

  @view
  getById(id: string): PaymentMethod | undefined {
    return getRoot<RootModelType>(this).entities.paymentMethods.get(
      id,
    );
  }
}

export const PaymentMethodListModel = model(PaymentMethodList);
